import React, {
  FC, useRef, useEffect, useState
} from 'react';
import classNames from 'classnames';
import styles from './pageListComponent.module.css';

type PropsType = {
  text: string;
  pageBackgroundUrl?: string;
  fontSize: number;
  lineHeight: number;
  indentRight: number;
  indentLeft: number;
  indentTop: number;
  fontType: string;
  isLoading: boolean;
  textColor: string;
  redLineIndent: number;
  indentLetter: number;
  indentWord: number;
  isRandomFontSize: boolean;
  isRandomRotateLetter: boolean;
  isRandomLineLeftIndent: boolean;
};

const getRandomArbitrary = (min: number, max: number) => Math.random() * (max - min) + min;

const getRandomValues = (words: string[], fontSize: number) => {
  const randomFontSizes: { [key: number]: number } = {};
  const randomRotateLetters: { [key: number]: number } = {};
  const randomIndentWords: { [key: number]: number } = {};

  words.forEach((word, wordIndex) => {
    randomIndentWords[wordIndex] = getRandomArbitrary(8, 17);

    word.split('').forEach((symbol, symbolIndex) => {
      const randomFontSize = getRandomArbitrary(fontSize - 4, fontSize + 4);
      const randomRotateLetter = getRandomArbitrary(-10, 10);

      const index = (wordIndex + 1) * (symbolIndex + 1);

      randomFontSizes[index] = randomFontSize;
      randomRotateLetters[index] = randomRotateLetter;
    });
  });

  return {
    randomFontSizes,
    randomRotateLetters,
    randomIndentWords,
  };
};

const DESKTOP_PAGE_WIDTH = 555;

const PageListComponent: FC<PropsType> = ({
  text,
  pageBackgroundUrl,
  fontType,
  isLoading,
  textColor,
  isRandomFontSize,
  isRandomRotateLetter,
  isRandomLineLeftIndent,
  ...props
}) => {
  const pageRef = useRef<HTMLImageElement>(null);
  const textContentRef = useRef<HTMLDivElement>(null);

  const [randomFontSizes, setRandomFontSizes] = useState<{ [key: number]: number }>({});
  const [randomRotateLetters, setRandomRotateLetters] = useState<{ [key: number]: number }>({});
  const [randomIndentWords, setRandomIndentWords] = useState<{ [key: number]: number }>({});

  const [maxLinesCount, setMaxLinesCount] = useState(0);
  const [pageWidth, setPageWidth] = useState(0);

  const getCalculateValue = (value: number) => {
    if (pageWidth < DESKTOP_PAGE_WIDTH) {
      return Math.round((pageWidth * value) / DESKTOP_PAGE_WIDTH);
    }

    return value;
  };

  const fontSize = getCalculateValue(props.fontSize);
  const lineHeight = getCalculateValue(props.lineHeight);
  const indentRight = getCalculateValue(props.indentRight);
  const indentLeft = getCalculateValue(isRandomLineLeftIndent ? props.indentLeft - 8 : props.indentLeft);
  const indentTop = getCalculateValue(props.indentTop);
  const redLineIndent = getCalculateValue(props.redLineIndent);
  const indentLetter = getCalculateValue(props.indentLetter);
  const indentWord = getCalculateValue(props.indentWord);

  const onCalculateLinesCount = () => {
    if (pageRef.current) {
      const pageSizes = pageRef.current.getBoundingClientRect();

      const linesCount = Math.floor((pageSizes.height - indentTop) / lineHeight);

      setMaxLinesCount(linesCount > 0 ? linesCount : 1);
      setPageWidth(pageSizes.width);
    }
  };

  useEffect(() => {
    onCalculateLinesCount();

    window.addEventListener('resize', onCalculateLinesCount);

    return () => {
      window.removeEventListener('resize', onCalculateLinesCount);
    };
  }, [
    fontSize,
    lineHeight,
    indentRight,
    indentLeft,
    indentTop,
    fontType,
    text,
    redLineIndent,
    isLoading,
    indentLetter,
    indentWord,
    isRandomFontSize,
    isRandomRotateLetter,
    pageRef,
  ]);

  const [computedWordPages, setComputedWordPages] = useState<string[]>([]);

  useEffect(() => {
    if (textContentRef.current && textContentRef.current && maxLinesCount && !isLoading) {
      let computedWords: string[] = [];
      let computedPages: string[] = [];
      const wordNodes = textContentRef.current.childNodes;
      let linesCounter = 1;
      let startWordNodesCoordinates = (textContentRef.current.childNodes[0] as HTMLElement).getBoundingClientRect();

      wordNodes.forEach((wordNode, index) => {
        const { y } = (wordNode as HTMLElement).getBoundingClientRect();

        if ((y !== startWordNodesCoordinates.y && wordNode.textContent !== '\n' || (wordNodes[index + 1] && wordNodes[index + 1].textContent === '\n' && wordNode.textContent === '\n'))) {
          startWordNodesCoordinates = (wordNode as HTMLElement).getBoundingClientRect();
          linesCounter++;
        }

        if (linesCounter <= maxLinesCount) {
          computedWords = [
            ...computedWords,
            ...[wordNode.textContent || ''],
          ];

          if (index === wordNodes.length - 1) {
            computedPages = [...computedPages, computedWords.join(' ')];
          }
        } else {
          computedPages = [
            ...computedPages,
            computedWords.join(' '),
          ];

          if (index === wordNodes.length - 1) {
            computedPages = [
              ...computedPages,
              ...[wordNode.textContent || ''],
            ];
          } else {
            computedWords = [wordNode.textContent || ''];
          }

          linesCounter = 1;
        }
      });

      setComputedWordPages(computedPages);
    }
  }, [
    fontSize,
    lineHeight,
    indentRight,
    indentLeft,
    indentTop,
    fontType,
    text,
    redLineIndent,
    isLoading,
    maxLinesCount,
    indentLetter,
    indentWord,
    isRandomFontSize,
    isRandomRotateLetter,
    pageRef,
    textContentRef,
    randomFontSizes,
    randomRotateLetters,
    randomIndentWords,
  ]);

  useEffect(() => {
    if (text.length) {
      const randomFonts = getRandomValues(words, fontSize).randomFontSizes;
      const randomRotates = getRandomValues(words, fontSize).randomRotateLetters;
      const randomIndents = getRandomValues(words, fontSize).randomIndentWords;

      setRandomFontSizes(randomFonts);
      setRandomRotateLetters(randomRotates);
      setRandomIndentWords(randomIndents);
    }
  }, [
    fontSize,
    text,
  ]);

  const pageStyles = {
    fontSize: `${fontSize}px`,
    lineHeight: `${lineHeight}px`,
    fontFamily: fontType,
    color: textColor,
    letterSpacing: indentLetter,
    wordSpacing: indentWord,
  };

  const words = text.split('\n').map((line) => `${line} \n`).reduce((result: string[], line: string) => [
    ...result,
    ...line.split(' ').map((word) => word),
  ], []).filter((line) => line);

  return (
        <div className={styles.container}>
            <img ref={pageRef} src={pageBackgroundUrl} className={classNames(styles.page, styles.hidden)} />

            <div
                style={pageStyles}
                className={classNames(styles.page, styles.hidden)}
            >
                <div
                    className={classNames(styles.container)}
                    style={{
                      paddingRight: `${indentRight + redLineIndent}px`,
                      paddingLeft: `${indentLeft}px`,
                      paddingTop: `${indentTop}px`,
                    }}
                >
                    <div ref={textContentRef}>
                        {words.map((pageText, indexLine) => (pageText === '\n' && indexLine !== 0
                          ? (
                                     <span key={indexLine}>
                                        <br />{'\n'}
                                    </span>
                          )
                          : (
                                     <span
                                         className={styles.text}
                                         key={indexLine}
                                         style={{
                                           maxHeight: `${lineHeight}px`,
                                           ...(isRandomLineLeftIndent ? {
                                             marginLeft: `${randomIndentWords[indexLine]}px`,
                                             marginRight: 0,
                                           } : {}),
                                         }}
                                     >{pageText.split('').map((symbol, indexSymbol) => {
                                       const index = (indexLine + 1) * (indexSymbol + 1);

                                       return (
                                             <span style={{
                                               ...(isRandomFontSize ? { fontSize: `${randomFontSizes[index]}px` } : {}),
                                               ...(isRandomRotateLetter ? { transform: `rotate(${randomRotateLetters[index]}deg)` } : {}),
                                               height: lineHeight,
                                               minHeight: lineHeight,
                                               maxHeight: lineHeight,
                                             }} className={styles.symbol} key={indexSymbol}>{symbol}</span>
                                       );
                                     })}</span>
                          )))}
                    </div>
                </div>
            </div>

            {computedWordPages.map((page, indexPage) => {
              const isEvenPage = indexPage % 2 !== 0;

              return (
                    <div
                        key={indexPage}
                        style={pageStyles}
                        className={classNames(styles.page, 'nodePage', { [styles.even]: isEvenPage })}
                    >
                        <img src={pageBackgroundUrl} />
                        <div
                            className={classNames(styles.container, styles.textContent)}
                            style={{
                              paddingRight: `${!isEvenPage ? indentRight + redLineIndent : indentRight}px`,
                              paddingLeft: `${isEvenPage ? indentLeft + redLineIndent : indentLeft}px`,
                              paddingTop: `${indentTop}px`,
                            }}
                        >
                            {page.split(' ').map((pageText, indexLine) => (pageText === '\n' && indexLine !== 0
                              ? (
                                    <span key={indexLine}>
                                        <br />
                                    </span>
                              )
                              : (
                                    <span
                                        className={styles.text}
                                        key={indexLine}
                                        style={{
                                          maxHeight: `${lineHeight}px`,
                                          ...(isRandomLineLeftIndent ? {
                                            marginLeft: `${randomIndentWords[indexLine]}px`,
                                            marginRight: 0,
                                          } : {}),
                                        }}
                                    >{pageText.split('').map((symbol, indexSymbol) => {
                                      const index = (indexLine + 1) * (indexSymbol + 1);

                                      return (
                                            <span style={{
                                              ...(isRandomFontSize ? { fontSize: `${randomFontSizes[index]}px` } : {}),
                                              ...(isRandomRotateLetter ? { transform: `rotate(${randomRotateLetters[index]}deg)` } : {}),
                                            }} className={styles.symbol} key={indexSymbol}>{symbol}</span>
                                      );
                                    })}</span>
                              )))}
                        </div>
                    </div>
              );
            })}
        </div>
  );
};

export default React.memo(PageListComponent);
